<template>
  <div
    ref="invoiceExportModal"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="invoiceExportModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5
            id="invoiceExportModalLabel"
            class="modal-title"
          >
            <i class="fa fa-file-invoice-dollar me-2" />
            Export Invoice
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          />
        </div>

        <div class="modal-body">
          <div class="row g-3 mb-3">
            <div class="col-md-6">
              <label class="form-label">Invoice Number</label>
              <input
                v-model="invoiceNumber"
                type="text"
                class="form-control form-control-sm"
                :disabled="isFetchingNumber"
                :placeholder="isFetchingNumber ? 'Loading…' : ''"
              >
            </div>
            <div class="col-md-6">
              <label class="form-label">Client Name</label>
              <input
                v-model="clientName"
                type="text"
                class="form-control form-control-sm"
                readonly
              >
            </div>
            <div class="col-md-6">
              <label class="form-label">Project Code</label>
              <input
                v-model="projectCode"
                type="text"
                class="form-control form-control-sm"
                placeholder="Optional"
              >
            </div>
            <div class="col-md-6">
              <label class="form-label">Date</label>
              <input
                v-model="issueDate"
                type="date"
                class="form-control form-control-sm"
              >
            </div>
            <div class="col-md-6">
              <label class="form-label">Type</label>
              <select
                v-model="projectType"
                class="form-select form-select-sm"
              >
                <option value="">
                  - Select type -
                </option>
                <option
                  v-for="pref in engagementPreferences"
                  :key="pref.id"
                  :value="pref.name"
                >
                  {{ pref.name }}
                </option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label">Terms (days)</label>
              <input
                v-model.number="termsDays"
                v-no-wheel
                type="number"
                min="1"
                class="form-control form-control-sm"
              >
            </div>
            <div class="col-md-6">
              <label class="form-label">Due Date</label>
              <input
                :value="dueDate"
                :disabled="true"
                type="date"
                class="form-control form-control-sm"
              >
            </div>
            <div class="col-12">
              <label class="form-label">Client Address</label>
              <textarea
                v-model="clientAddress"
                class="form-control form-control-sm"
                rows="3"
              />
            </div>
          </div>

          <div
            v-if="isLoading"
            class="text-center py-5"
          >
            <div
              class="spinner-border text-primary"
              role="status"
            >
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <div
            v-else-if="rows.length === 0"
            class="empty-state py-4 text-center"
          >
            <i class="fa fa-inbox fa-3x text-muted mb-3" />
            <p class="text-muted mb-0">
              No completed payment requests for this project.
            </p>
          </div>
          <table
            v-else
            id="invoice-export-table"
            class="table table-sm table-hover align-middle"
          >
            <thead>
              <tr>
                <th style="width: 36px;" />
                <th>Expert</th>
                <th>Email</th>
                <th>Credits</th>
                <th>Rate (USD)</th>
                <th>Duration</th>
                <th class="text-end">
                  Amount (USD)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.payment_request_id"
              >
                <td>
                  <input
                    v-model="selectedIds"
                    type="checkbox"
                    :value="row.payment_request_id"
                  >
                </td>
                <td>{{ row.expert_name }}</td>
                <td>{{ row.email }}</td>
                <td>{{ row.credits }}</td>
                <td>$ {{ formatAmount(row.rate) }}</td>
                <td>{{ row.duration }} minutes</td>
                <td class="text-end">
                  <input
                    v-model.number="row.amount"
                    v-no-wheel
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control form-control-sm text-end"
                    style="width: 100px; display: inline-block;"
                  >
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th
                  colspan="3"
                  class="text-end"
                >
                  Total selected
                </th>
                <th class="text-end">
                  {{ formatAmount(selectedTotal) }}
                </th>
                <th />
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            :disabled="isExporting"
          >
            <i class="fa fa-times me-1" />
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="selectedIds.length === 0 || isExporting"
            @click="onExport"
          >
            <i class="fa fa-file-pdf me-1" />
            <span v-if="isExporting">Generating...</span>
            <span v-else>Export PDF</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";

export default {
    name: "InvoiceExportModal",
    props: {
        projectId: {
            type: [Number, String],
            required: true,
        },
        expertId: {
            type: [Number, String],
            default: null,
        },
        preselectedPaymentRequestIds: {
            type: Array,
            default: null,
        },
    },
    data() {
        return {
            modalInstance: null,
            exportSucceeded: false,
            isLoading: false,
            isExporting: false,
            rows: [],
            selectedIds: [],
            invoiceNumber: "",
            issueDate: "",
            termsDays: 30,
            clientAddress: "",
            clientName: "",
            projectCode: "",
            engagementPreferences: [],
            projectType: "",
            isFetchingNumber: false,
        };
    },
    computed: {
        allSelected() {
            return this.rows.length > 0 && this.selectedIds.length === this.rows.length;
        },
        selectedTotal() {
            const selectedSet = new Set(this.selectedIds.map(Number));
            return this.rows
                .filter((row) => selectedSet.has(Number(row.payment_request_id)))
                .reduce((sum, row) => sum + Number(row.amount || 0), 0);
        },
        dueDate() {
            if (!this.issueDate || !this.termsDays) return "";
            const d = new Date(this.issueDate);
            if (Number.isNaN(d.getTime())) return "";
            d.setDate(d.getDate() + parseInt(this.termsDays, 10));
            return d.toISOString().slice(0, 10);
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.modalInstance = new Modal(this.$refs.invoiceExportModal);
            this._onShown = () => this.fetchEligible();
            this._onHidden = () => this.onModalHidden();
            this.$refs.invoiceExportModal.addEventListener("shown.bs.modal", this._onShown);
            this.$refs.invoiceExportModal.addEventListener("hidden.bs.modal", this._onHidden);
        });
    },
    beforeDestroy() {
        const modalElement = this.$refs.invoiceExportModal;
        if (modalElement) {
            modalElement.removeEventListener("shown.bs.modal", this._onShown);
            modalElement.removeEventListener("hidden.bs.modal", this._onHidden);
        }
    },
    methods: {
        show() {
            if (this.modalInstance) {
                this.modalInstance.show();
            }
        },
        hide() {
            if (this.modalInstance) {
                this.modalInstance.hide();
            }
        },
        onModalHidden() {
            if (this.exportSucceeded) {
                this.$emit("export-success");
            } else {
                this.$emit("export-cancelled");
            }
            this.exportSucceeded = false;
            this.reset();
        },
        reset() {
            this.rows = [];
            this.selectedIds = [];
            this.isLoading = false;
            this.isExporting = false;
            this.invoiceNumber = "";
            this.issueDate = "";
            this.termsDays = 30;
            this.clientAddress = "";
            this.clientName = "";
            this.projectCode = "";
            this.engagementPreferences = [];
            this.projectType = "";
            this.isFetchingNumber = false;
        },
        formatAmount(value) {
            return Number(value || 0).toFixed(2);
        },
        toggleAll(checked) {
            this.selectedIds = checked ? this.rows.map((row) => row.payment_request_id) : [];
        },
        fetchEligible() {
            if (this.projectId == null || this.projectId === "") {
                this.rows = [];
                this.isLoading = false;
                return;
            }

            this.issueDate = new Date().toISOString().slice(0, 10);
            this.termsDays = 30;

            this.isFetchingNumber = true;
            axios
                .get(`/admin/project/${this.projectId}/invoice/next-number`)
                .then((response) => {
                    this.invoiceNumber = response?.data?.data?.invoice_number || "";
                })
                .catch(() => {
                    this.invoiceNumber = "";
                })
                .finally(() => {
                    this.isFetchingNumber = false;
                });

            this.isLoading = true;
            const eligibleUrl = this.expertId != null
                ? `/admin/project/${this.projectId}/invoice/eligible?expert_id=${this.expertId}`
                : `/admin/project/${this.projectId}/invoice/eligible`;
            axios
                .get(eligibleUrl)
                .then((response) => {
                    this.rows = response?.data?.data || [];
                    this.clientAddress = response?.data?.meta?.client_address || "";
                    this.clientName = response?.data?.meta?.client_firm_name || "";
                    this.engagementPreferences = response?.data?.meta?.engagement_preferences || [];
                    this.projectType = response?.data?.meta?.project_type_name || "";
                    const availableIds = this.rows.map((row) => row.payment_request_id);

                    if (Array.isArray(this.preselectedPaymentRequestIds)) {
                        const preselected = new Set(this.preselectedPaymentRequestIds.map(Number));
                        this.selectedIds = availableIds.filter((id) => preselected.has(Number(id)));
                    } else {
                        this.selectedIds = availableIds;
                    }
                })
                .catch(() => {
                    this.rows = [];
                    this.engagementPreferences = [];
                    this.projectType = "";
                    if (typeof One !== "undefined") {
                        One.helpers("jq-notify", {
                            type: "danger",
                            icon: "fa fa-times me-1",
                            message: "Failed to load eligible payment requests.",
                        });
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        async onExport() {
            if (this.selectedIds.length === 0 || this.projectId == null || this.projectId === "") {
                return;
            }

            this.isExporting = true;
            try {
                const amounts = {};
                this.selectedIds.forEach((id) => {
                    const row = this.rows.find((r) => r.payment_request_id === id);
                    if (row) amounts[id] = row.amount;
                });

                const response = await axios.post(
                    `/admin/project/${this.projectId}/invoice/export`,
                    {
                        payment_request_ids: this.selectedIds,
                        invoice_number: this.invoiceNumber,
                        issue_date: this.issueDate,
                        terms_days: this.termsDays,
                        due_date: this.dueDate,
                        client_address: this.clientAddress,
                        client_name: this.clientName,
                        project_code: this.projectCode,
                        type: this.projectType,
                        amounts,
                    },
                    {
                        responseType: "blob",
                    }
                );

                const disposition = response.headers["content-disposition"] || "";
                const filenameMatch = /filename="?([^"]+)"?/.exec(disposition);
                const filename = filenameMatch ? filenameMatch[1] : "invoice.pdf";

                const blob = new Blob([response.data], { type: "application/pdf" });
                const fileUrl = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = fileUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(fileUrl);

                if (typeof One !== "undefined") {
                    One.helpers("jq-notify", {
                        type: "success",
                        icon: "fa fa-check me-1",
                        message: `Invoice ${filename.replace(/\.pdf$/, "")} generated.`,
                    });
                }
                this.exportSucceeded = true;
                this.hide();
            } catch (error) {
                if (typeof One !== "undefined") {
                    One.helpers("jq-notify", {
                        type: "danger",
                        icon: "fa fa-times me-1",
                        message: "Export failed.",
                    });
                }
            } finally {
                this.isExporting = false;
            }
        },
    },
};
</script>
